import React, { useState } from "react";
import {
  Typography,
  Box,
  Paper,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Container,
  Link
} from "@mui/material";
import { sendPasswordResetEmail } from "firebase/auth";
import { Link as RouterLink } from "react-router-dom";
import { useFirebase } from "./contexts/FirebaseContext";

function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const { auth } = useFirebase();

  const validateForm = () => {
    // Reset error
    setError(null);

    // Check if email is filled
    if (!email) {
      setError("Email is required");
      return false;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError("Please enter a valid email address");
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      // Send password reset email
      await sendPasswordResetEmail(auth, email, {
        url: `${window.location.origin}/`,
        handleCodeInApp: false
      });
      
      // Show success message
      setSuccess(true);
    } catch (error) {
      console.error("Error sending password reset email:", error);
      
      // Handle specific Firebase auth errors
      switch (error.code) {
        case "auth/invalid-email":
          setError("Invalid email address format");
          break;
        case "auth/user-not-found":
          setError("No account found with this email");
          break;
        default:
          setError("Failed to send password reset email. Please try again later");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="sm">
      <Box sx={{ mt: 8, mb: 4 }}>
        <Paper elevation={3} sx={{ p: 4 }}>
          <Typography variant="h4" component="h1" gutterBottom align="center">
            Reset Password
          </Typography>
          
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          
          {success ? (
            <>
              <Alert severity="success" sx={{ mb: 2 }}>
                Password reset email sent! Check your inbox.
              </Alert>
              <Typography variant="body1" paragraph>
                We've sent a password reset link to <strong>{email}</strong>. Please check your email and follow the instructions to reset your password.
              </Typography>
              <Typography variant="body2">
                Didn't receive the email? Check your spam folder or <Link component="button" onClick={handleSubmit}>send again</Link>.
              </Typography>
              <Box sx={{ mt: 3, textAlign: 'center' }}>
                <Link component={RouterLink} to="/" underline="hover">
                  Return to Login
                </Link>
              </Box>
            </>
          ) : (
            <Box component="form" onSubmit={handleSubmit} noValidate>
              <Typography variant="body1" paragraph>
                Enter your email address and we'll send you a link to reset your password.
              </Typography>
              
              <TextField
                margin="normal"
                required
                fullWidth
                label="Email Address"
                name="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={loading}
                autoFocus
              />
              
              <Button
                type="submit"
                fullWidth
                variant="contained"
                sx={{ mt: 3, mb: 2 }}
                disabled={loading}
              >
                {loading ? <CircularProgress size={24} /> : "Send Reset Link"}
              </Button>
              
              <Box sx={{ textAlign: 'center' }}>
                <Link component={RouterLink} to="/" underline="hover">
                  Back to Login
                </Link>
              </Box>
            </Box>
          )}
        </Paper>
      </Box>
    </Container>
  );
}

export default ForgotPassword; 